import React, { useEffect, useRef, useState } from "react";
import EditContactDrawer from "./EditContactDrawer";
import ContactDetailsDrawer from "./ContactDetailsDrawer";
import useContactData, { FormikContact } from "../../../hooks/useContactData";
import { ContactRouterDrawerType } from "../../../enums/ContactRouterDrawerType";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import ContactType from "enums/ContactType";
import { InviteContactModal } from "./ContactModules";
import { useComments } from "hooks/useComments";
import { ParentType } from "enums/Comments";
import { ConfirmDialog } from "@outpave/ds";
import { toastMutationError } from "utils/toastMutationError";

type RouterDrawer = "edit" | "details" | "businessEdit" | "businessDetails";

interface ContactDrawerRouterProps {
  open: boolean;
  drawerType: ContactRouterDrawerType;
  contactId?: string;
  startIndividualWithBusinessId?: string;
  onClose: (refresh?: boolean, contactIdToOpen?: string) => void;
  refreshContacts?: () => void;
  /** Fires once the router's open drawer has slid out after the router closes. */
  onAfterClose?: () => void;
}

const ContactDrawerRouter: React.FC<ContactDrawerRouterProps> = ({
  open,
  drawerType,
  contactId,
  startIndividualWithBusinessId,
  onClose,
  refreshContacts,
  onAfterClose,
}) => {
  const { t } = useTranslation();

  const [step, setStep] = useState<"edit" | "review">("edit");
  const [businessStep, setBusinessStep] = useState<"edit" | "review">("edit");
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [openAddBusinessDrawer, setOpenAddBusinessDrawer] = useState(false);
  const [openInviteContactModal, setOpenInviteContactModal] = useState(false);

  const {
    formikContact,
    customFieldSettings,
    specificLoadingStates,
    handleCreateContact,
    handleUpdateContact,
    handleReactivateContact,
  } = useContactData({ contactId });

  // Separate formik for business creation
  const {
    formikContact: businessFormik,
    customFieldSettings: businessCustomFieldSettings,
    specificLoadingStates: businessLoadingStates,
  } = useContactData({});

  const commentActions = useComments("", ParentType.CONTACT);
  const businessCommentActions = useComments("", ParentType.CONTACT);

  const handleOnClose = (refresh?: boolean, contactIdToOpen?: string) => {
    formikContact.resetForm();
    businessFormik.resetForm();
    commentActions.reset();
    onClose(refresh, contactIdToOpen);
  };

  const createContact = async (values: FormikContact) => {
    try {
      const result = await handleCreateContact(values);
      if (!result) {
        toast.error(t("Failed to create contact"));
        return;
      }

      const newContactId = result?.data?.addContact?.id;
      if (newContactId && commentActions.hasDraft) {
        await commentActions.submit(newContactId);
      }
      refreshContacts && refreshContacts();
      toast.success(t("Contact created successfully"));
      handleOnClose(true, newContactId);
    } catch (err: unknown) {
      // The drawer stays open, so the form the save failed on is still there.
      toastMutationError(err, t("Failed to create contact"));
    }
  };

  const updateContact = async (values: FormikContact) => {
    try {
      const result = await handleUpdateContact(values);
      if (!result) {
        toast.error(t("Failed to update contact"));
        return;
      }

      refreshContacts && refreshContacts();
      toast.success(t("Contact updated successfully"));
      handleOnClose(true, contactId);
      setIsEditing(false);
    } catch (err: unknown) {
      toastMutationError(err, t("Failed to update contact"));
    }
  };

  const reactivateContact = async (contactId: string) => {
    try {
      const result = await handleReactivateContact(contactId);
      if (!result) {
        toast.error(t("Failed to reactivate contact"));
        return;
      }

      refreshContacts && refreshContacts();
      toast.success(t("Contact reactivated successfully"));
      if (
        result.data.reactivateContact.contactType === ContactType.Individual
      ) {
        handleOnClose(true, result.data.reactivateContact.id);
      }
    } catch (err: unknown) {
      toastMutationError(err, t("Failed to reactivate contact"));
    }
  };

  const handleSaveAddBusinessToIndividual = async () => {
    try {
      const result = await handleCreateContact(businessFormik.values);
      if (!result) {
        toast.error(t("Failed to create business contact"));
        return;
      }

      const newBusinessId = result?.data?.addContact?.id;
      const newBusinessName = businessFormik.values.businessName;
      if (newBusinessId && businessCommentActions.hasDraft) {
        await businessCommentActions.submit(newBusinessId);
      }
      // Attach to the individual's formik
      formikContact.setFieldValue("parentContactId", newBusinessId);
      formikContact.setFieldValue("businessName", newBusinessName);
      toast.success(t("Business contact created and linked!"));
      setOpenAddBusinessDrawer(false);
      businessFormik.resetForm();
      businessCommentActions.reset();
      setBusinessStep("edit");
    } catch (err: unknown) {
      // Nothing is linked to the individual, so the half-filled business form
      // stays open rather than being reset out from under a retry.
      toastMutationError(err, t("Failed to create business contact"));
    }
  };

  // Edit Contact Drawer Handlers

  const handleSaveEditContactDrawer = async () => {
    // New contact go to review flow
    if (drawerType === ContactRouterDrawerType.NewContact && step === "edit") {
      setStep("review");
    }
    // Editing existing contact update flow
    else if (drawerType === ContactRouterDrawerType.EditingContact || isEditing)
      await updateContact(formikContact.values);
  };

  const handleCancelEditContactDrawer = (bypassDirtyCheck: boolean = false) => {
    // cancel new contact flow
    if (drawerType === ContactRouterDrawerType.NewContact && step === "edit") {
      if (formikContact.dirty && !bypassDirtyCheck) {
        setShowUnsavedModal(true);
      } else {
        formikContact.resetForm();
        handleOnClose(false);
      }
    }
    // editing existing/editing contact flow
    else if (
      drawerType === ContactRouterDrawerType.EditingContact ||
      isEditing
    ) {
      if (formikContact.dirty && !bypassDirtyCheck) {
        setShowUnsavedModal(true);
      } else {
        if (isEditing) {
          setIsEditing(false);
        } else {
          handleOnClose(false);
        }
      }
    }
  };

  // Contact Details Drawer Handlers

  const handleSaveContactDetailsDrawer = async () => {
    // Create new contact from review flow
    if (
      drawerType === ContactRouterDrawerType.NewContact &&
      step === "review"
    ) {
      await createContact(formikContact.values);
    }
    // Updating existing contact
    else if (
      drawerType === ContactRouterDrawerType.ExistingContact &&
      !isEditing
    ) {
      await updateContact(formikContact.values);
    }
  };

  const handleCancelContactDetailsDrawer = () => {
    // New contact going back to edit flow
    if (
      drawerType === ContactRouterDrawerType.NewContact &&
      step === "review"
    ) {
      setStep("edit");
    }
    // Existing or Editing contact closes drawer
    else if (
      drawerType === ContactRouterDrawerType.ExistingContact &&
      !isEditing
    ) {
      handleOnClose(false);
    }
  };

  const handleEditContactDetailsDrawer = () => {
    // Editing existing contact
    if (drawerType === ContactRouterDrawerType.ExistingContact && !isEditing) {
      setIsEditing(true);
    }
  };

  // Add Business to Individual Drawer Handlers

  const handleOpenAddSaveNewBusinessDrawer = () => {
    businessFormik.setFieldValue("contactType", ContactType.Business);
    setOpenAddBusinessDrawer(true);
  };

  const handleCancelAddBusinessToIndividual = () => {
    setOpenAddBusinessDrawer(false);
    businessFormik.resetForm();
    businessCommentActions.reset();
  };

  useEffect(() => {
    if (
      startIndividualWithBusinessId &&
      drawerType === ContactRouterDrawerType.NewContact &&
      open
    ) {
      formikContact.setFieldValue(
        "parentContactId",
        startIndividualWithBusinessId,
      );
      formikContact.setFieldValue("contactType", ContactType.Individual);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startIndividualWithBusinessId, drawerType, open]);

  // Each pair is exclusive on purpose: the review step and the edit step are
  // the same drawer position, so letting both be true stacks two drawers and
  // leaves the lower one's footer wired to handlers that early-return.
  const showEditContactDrawer =
    (drawerType === ContactRouterDrawerType.NewContact && step === "edit") ||
    drawerType === ContactRouterDrawerType.EditingContact ||
    isEditing;

  const showContactDetailsDrawer =
    (drawerType === ContactRouterDrawerType.NewContact && step === "review") ||
    (drawerType === ContactRouterDrawerType.ExistingContact && !isEditing);

  const showAddBusinessToIndividualEditContactDrawer =
    openAddBusinessDrawer && businessStep === "edit";
  const showBusinessDetailsDrawer =
    openAddBusinessDrawer && businessStep === "review";

  // A business drawer only stacks over the contact's own, so the contact's drawer is the one to report.
  const shownDrawer: RouterDrawer | undefined = showEditContactDrawer
    ? "edit"
    : showContactDetailsDrawer
      ? "details"
      : showAddBusinessToIndividualEditContactDrawer
        ? "businessEdit"
        : showBusinessDetailsDrawer
          ? "businessDetails"
          : undefined;
  // Latched while open: switching drawers closes one too, and that one may still be
  // sliding out when the router closes, so only the drawer shown at the close reports.
  const closingDrawer = useRef<RouterDrawer>();
  if (open) closingDrawer.current = shownDrawer;
  const afterRouterClose = (drawer: RouterDrawer) =>
    !open && closingDrawer.current === drawer ? onAfterClose : undefined;

  return (
    <>
      <EditContactDrawer
        open={showEditContactDrawer && open}
        onAfterClose={afterRouterClose("edit")}
        drawerType={drawerType}
        formik={formikContact}
        customFieldSettings={customFieldSettings}
        specificLoadingStates={specificLoadingStates}
        startIndividualWithBusinessId={!!startIndividualWithBusinessId}
        handleOpenAddSaveNewBusinessDrawer={handleOpenAddSaveNewBusinessDrawer}
        reactivateContact={reactivateContact}
        onSave={handleSaveEditContactDrawer}
        onCancel={handleCancelEditContactDrawer}
      />

      <ContactDetailsDrawer
        open={showContactDetailsDrawer && open}
        onAfterClose={afterRouterClose("details")}
        drawerType={drawerType}
        formikContact={
          businessStep === "review" ? businessFormik : formikContact
        }
        specificLoadingStates={specificLoadingStates}
        onCancel={handleCancelContactDetailsDrawer}
        onSave={handleSaveContactDetailsDrawer}
        onEdit={handleEditContactDetailsDrawer}
        onInvite={() => setOpenInviteContactModal(true)}
        commentActions={commentActions}
      />

      {/* Add Business to Individual Edit */}
      <EditContactDrawer
        open={showAddBusinessToIndividualEditContactDrawer && open}
        onAfterClose={afterRouterClose("businessEdit")}
        drawerType={ContactRouterDrawerType.NewContact}
        formik={businessFormik}
        customFieldSettings={businessCustomFieldSettings}
        specificLoadingStates={businessLoadingStates}
        addingBusinesstoIndividual={true}
        reactivateContact={reactivateContact}
        onCancel={handleCancelAddBusinessToIndividual}
        onSave={() => setBusinessStep("review")}
      />

      {/* Add Business to Individual review */}
      <ContactDetailsDrawer
        open={showBusinessDetailsDrawer && open}
        onAfterClose={afterRouterClose("businessDetails")}
        drawerType={ContactRouterDrawerType.NewContact}
        formikContact={businessFormik}
        specificLoadingStates={businessLoadingStates}
        onCancel={() => setBusinessStep("edit")}
        onSave={handleSaveAddBusinessToIndividual}
        commentActions={businessCommentActions}
      />

      <ConfirmDialog
        open={showUnsavedModal}
        tone="primary"
        title={t("Unsaved Changes")}
        message={t("You have unsaved changes, are you sure you want to leave?")}
        confirmLabel={t("Yes")}
        cancelLabel={t("No")}
        onConfirm={() => {
          setShowUnsavedModal(false);
          formikContact.resetForm();
          handleCancelEditContactDrawer(true);
        }}
        onCancel={() => setShowUnsavedModal(false)}
      />

      {contactId && (
        <InviteContactModal
          openInviteContactModal={openInviteContactModal}
          setOpenInviteContactModal={setOpenInviteContactModal}
          contact={{
            id: contactId,
            email: formikContact.values.email,
            contactType: formikContact.values.contactType,
          }}
        />
      )}
    </>
  );
};

export default ContactDrawerRouter;
